/**
 * Marquee scrolling text element
 * Creates infinite horizontal scrolling animation with configurable speed and pause on hover
 *
 * The animation only runs while the marquee is on screen, un-paused and the tab is visible.
 * An ungated loop writes a transform to a composited, full-width layer every frame for the
 * life of the page, which on iOS accumulates paint pressure until the tab is killed.
 */

const VIEWPORT_MARGIN = '200px'
const MAX_COPIES = 8
const MAX_FRAME_DELTA_MS = 50

export class MarqueeElement extends HTMLElement {
  private animationId: number | null = null
  private lastFrameTime: number | null = null
  private isRunning: boolean = false
  private scrollPosition: number = 0
  private content: HTMLElement | null = null
  private originalSet: HTMLElement | null = null
  private autoplaySpeed: number = 3000
  private pauseOnHover: boolean = true
  private hoverBound: boolean = false
  private isPaused: boolean = false
  private isOnScreen: boolean = false
  private contentWidth: number = 0
  private resizeObserver: ResizeObserver | null = null
  private intersectionObserver: IntersectionObserver | null = null
  private widthTimer: number | null = null
  private isSetup: boolean = false

  private handleMouseEnter = () => this.pause()
  private handleMouseLeave = () => this.resume()
  private handleVisibilityChange = () => this.syncAnimationState()

  connectedCallback() {
    this.setupMarquee()
    this.setupIntersectionObserver()
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    this.bindHoverListeners()
    this.syncAnimationState()
  }

  disconnectedCallback() {
    this.stopAnimation()
    this.clearWidthTimer()
    this.unbindHoverListeners()

    this.resizeObserver?.disconnect()
    this.resizeObserver = null

    this.intersectionObserver?.disconnect()
    this.intersectionObserver = null

    this.isOnScreen = false
    this.isPaused = false

    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  }

  private setupMarquee() {
    const speedAttr = this.getAttribute('data-autoplay-speed')
    if (speedAttr) {
      this.autoplaySpeed = parseInt(speedAttr, 10) || 3000
    }

    this.pauseOnHover = this.getAttribute('data-pause-hover') !== 'false'

    if (this.isSetup && this.content?.isConnected && this.originalSet?.isConnected) {
      this.setupResizeObserver()
      this.ensureCopies()
      return
    }

    this.clearWidthTimer()

    const slides = Array.from(this.childNodes).filter(
      (node): node is Element => node.nodeType === Node.ELEMENT_NODE
    )

    if (slides.length === 0) return

    this.content = document.createElement('div')
    this.content.style.cssText = `
      display: inline-flex;
      align-items: center;
      white-space: nowrap;
      min-width: max-content;
    `

    this.originalSet = document.createElement('div')
    this.originalSet.style.cssText = `
      display: inline-flex;
      align-items: center;
      flex-shrink: 0;
    `

    slides.forEach(slide => {
      this.originalSet!.appendChild(slide)
    })

    this.content.appendChild(this.originalSet)
    this.appendChild(this.content)

    this.setupResizeObserver()

    this.widthTimer = window.setTimeout(() => {
      this.widthTimer = null
      this.ensureCopies()
    }, 0)

    this.isSetup = true
  }

  private bindHoverListeners() {
    this.unbindHoverListeners()
    if (!this.pauseOnHover) return

    this.addEventListener('mouseenter', this.handleMouseEnter)
    this.addEventListener('mouseleave', this.handleMouseLeave)
    this.hoverBound = true
  }

  private unbindHoverListeners() {
    if (!this.hoverBound) return

    this.removeEventListener('mouseenter', this.handleMouseEnter)
    this.removeEventListener('mouseleave', this.handleMouseLeave)
    this.hoverBound = false
  }

  private clearWidthTimer() {
    if (this.widthTimer === null) return

    window.clearTimeout(this.widthTimer)
    this.widthTimer = null
  }

  private setupResizeObserver() {
    if (!('ResizeObserver' in window) || !this.originalSet) return

    this.resizeObserver?.disconnect()
    this.resizeObserver = new ResizeObserver(() => {
      this.ensureCopies()
    })
    this.resizeObserver.observe(this.originalSet)
  }

  private setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      this.isOnScreen = true
      return
    }

    this.intersectionObserver?.disconnect()
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        this.isOnScreen = entries.some((entry) => entry.isIntersecting)
        this.syncAnimationState()
      },
      { rootMargin: VIEWPORT_MARGIN }
    )
    this.intersectionObserver.observe(this)
  }

  private ensureCopies() {
    if (!this.content || !this.originalSet) return

    const setWidth = this.originalSet.getBoundingClientRect().width
    if (setWidth <= 0) return

    this.contentWidth = setWidth

    const viewportWidth = this.getBoundingClientRect().width
    const needed = Math.min(
      MAX_COPIES,
      Math.max(2, Math.ceil((viewportWidth + setWidth) / setWidth) + 1)
    )

    while (this.content.childElementCount < needed) {
      this.content.appendChild(this.originalSet.cloneNode(true))
    }

    if (this.scrollPosition >= this.contentWidth) {
      this.scrollPosition = this.scrollPosition % this.contentWidth
    }
  }

  /**
   * Single source of truth for whether the loop should be running.
   */
  private syncAnimationState() {
    const shouldAnimate = this.isOnScreen && !this.isPaused && !document.hidden

    if (shouldAnimate) this.startAnimation()
    else this.stopAnimation()
  }

  private startAnimation() {
    if (this.isRunning) return

    this.isRunning = true
    this.lastFrameTime = null

    if (this.content) {
      this.content.style.willChange = 'transform'
    }

    const animate = (now: number) => {
      if (!this.isRunning) return

      if (this.lastFrameTime === null) this.lastFrameTime = now
      const delta = Math.min(now - this.lastFrameTime, MAX_FRAME_DELTA_MS)
      this.lastFrameTime = now

      if (this.content && this.contentWidth > 0) {
        const pxPerSecond = Math.max(0.2, 3000 / this.autoplaySpeed) * 60
        this.scrollPosition += pxPerSecond * (delta / 1000)

        if (this.scrollPosition >= this.contentWidth) {
          this.scrollPosition %= this.contentWidth
        }

        this.content.style.transform = `translateX(-${this.scrollPosition}px)`
      }

      if (!this.isRunning) return
      this.animationId = requestAnimationFrame(animate)
    }

    this.animationId = requestAnimationFrame(animate)
  }

  private stopAnimation() {
    this.isRunning = false
    this.lastFrameTime = null

    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }

    if (this.content) {
      this.content.style.willChange = 'auto'
    }
  }

  private pause() {
    this.isPaused = true
    this.syncAnimationState()
  }

  private resume() {
    this.isPaused = false
    this.syncAnimationState()
  }

  public pauseMarquee() {
    this.pause()
  }

  public resumeMarquee() {
    this.resume()
  }

  public setSpeed(speed: number) {
    this.autoplaySpeed = speed
  }

  public recalculateWidth() {
    this.ensureCopies()
  }
}
